/**
 * MessagesLayer — read-only display of player-planted map messages.
 *
 * Messages live in the `ptw-messages` Firestore collection (one doc per user,
 * publicly readable). This layer fetches messages near the viewport with a
 * one-shot getDocs query (NO onSnapshot — no realtime subscription, no writes)
 * and renders them as a Mapbox symbol layer.
 *
 * Fetch strategy: on map `moveend` (debounced), if zoom >= MESSAGE_MIN_ZOOM,
 * query by geohash bands around the map center (~3 km radius), capped at
 * ~200 docs total.
 *
 * Rendering/style-change lifecycle is delegated to GeoJsonLayer, which re-adds
 * the source + layer after a map style switch (same approach as TilesLayer).
 *
 * Z-ORDER: mount AFTER TilesLayer in MapView.tsx — map.addLayer() order follows
 * JSX order, and messages must render on top of tiles.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { getDocs } from 'firebase/firestore';
import { geohashQueryBounds } from 'geofire-common';
import { COLLECTIONS, MESSAGE_MIN_ZOOM } from '@painttheworld/shared';
import { db } from '../../../backend/firebase';
import { buildGeohashQuery } from '../../../redux/features/firestore/helpers/buildGeohashQuery';
import MapElementProps from '../MapElementProps';
import GeoJsonLayer from './GeoJsonLayer';

// Search radius around the map center for nearby messages.
const MESSAGES_RADIUS_METERS = 3000;
// Hard cap on messages rendered per fetch (also the per-band query limit).
const MESSAGES_MAX_DOCS = 200;
// Minimum ms between Firestore fetches while the user pans/zooms.
const REFETCH_DEBOUNCE_MS = 2000;

const EMPTY_COLLECTION: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
    type: 'FeatureCollection',
    features: [],
};

const MessagesLayer = ({ map }: MapElementProps) => {
    const [data, setData] = useState<GeoJSON.FeatureCollection<GeoJSON.Geometry>>(EMPTY_COLLECTION);
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const fetchMessages = useCallback(async () => {
        if (!map) return;
        // Below the visibility threshold there is nothing to show — skip the query.
        if (map.getZoom() < MESSAGE_MIN_ZOOM) return;

        const center = map.getCenter();
        const bands = geohashQueryBounds([center.lat, center.lng], MESSAGES_RADIUS_METERS);

        // One-shot reads only — web is read-only and messages don't need realtime.
        const snapshots = await Promise.all(bands.map((band) =>
            getDocs(buildGeohashQuery(db, COLLECTIONS.MESSAGES, band, MESSAGES_MAX_DOCS))
        ));

        const seen = new Set<string>();
        const features: GeoJSON.Feature<GeoJSON.Geometry>[] = [];
        for (const snapshot of snapshots) {
            for (const docSnap of snapshot.docs) {
                if (features.length >= MESSAGES_MAX_DOCS) break;
                if (seen.has(docSnap.id)) continue; // geohash bands can overlap
                seen.add(docSnap.id);

                const message = docSnap.data();
                if (!message?.text || !message?.coordinate) continue;

                features.push({
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: [message.coordinate.longitude, message.coordinate.latitude],
                    },
                    properties: {
                        text: message.text,
                        color: message.colorHex ?? '#FFFFFF',
                    },
                });
            }
            if (features.length >= MESSAGES_MAX_DOCS) break;
        }

        setData({ type: 'FeatureCollection', features });
    }, [map]);

    useEffect(() => {
        if (!map) return;

        const safeFetch = () => {
            fetchMessages().catch((error) => {
                console.error('[MessagesLayer] fetch error:', error);
            });
        };

        const onMoveEnd = () => {
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
            debounceTimer.current = setTimeout(() => {
                debounceTimer.current = null;
                safeFetch();
            }, REFETCH_DEBOUNCE_MS);
        };

        map.on('moveend', onMoveEnd);
        // Initial fetch — moveend may not fire until the user interacts.
        safeFetch();

        return () => {
            map.off('moveend', onMoveEnd);
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
                debounceTimer.current = null;
            }
        };
    }, [map, fetchMessages]);

    return (
        <GeoJsonLayer map={map} data={data} layer={{
            id: 'MessagesLayer',
            type: 'symbol',
            minzoom: MESSAGE_MIN_ZOOM,
            layout: {
                'text-field': ['get', 'text'],
                'text-size': 14,
                'text-max-width': 12,
            },
            paint: {
                'text-color': ['get', 'color'],
                'text-halo-color': 'rgba(0,0,0,0.9)',
                'text-halo-width': 1.5,
            },
        } as any} />
    );
};

export default MessagesLayer;
